// components/RatingForm.tsx

import { useState, FormEvent } from "react";
import RatingStars from "./RatingStars";
import { Review } from "@/types/types";

interface RatingFormProps {
  onSubmit: (data: Partial<Review>) => void;
}

const RatingForm: React.FC<RatingFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<Partial<Review>>({
    author: "",
    email: "",
    rating: 0,
    title: "",
    content: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    author: "",
    email: "",
    rating: "",
    title: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validation logic
  const validate = () => {
    let newErrors = { author: "", email: "", rating: "", title: "" };
    let isValid = true;

    if (!formData.author) {
      newErrors.author = "Author is required";
      isValid = false;
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    }
    if (formData.rating === 0) {
      newErrors.rating = "Rating is required";
      isValid = false;
    }
    if (!formData.title) {
      newErrors.title = "Review Title is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <form
      className="flex flex-col gap-4 bg-white p-4 shadow-md rounded w-full max-w-[500px] mx-auto"
      onSubmit={handleSubmit}
    >
      <div>
        <label className="block text-gray-700 mb-2 uppercase font-[600] text-[14px]">
          Name
        </label>
        <input
          type="text"
          name="author"
          placeholder="Enter your name"
          className="border-2 border-gray-300 p-2 rounded w-full"
          value={formData.author}
          onChange={handleChange}
        />
        {errors.author && (
          <p className="text-red-500 text-xs italic mt-1">{errors.author}</p>
        )}
      </div>
      <div>
        <label className="block text-gray-700 mb-2 uppercase font-[600] text-[14px]">
          Email
        </label>
        <input
          type="email"
          name="email"
          placeholder="Enter your email"
          className="border-2 border-gray-300 p-2 rounded w-full"
          value={formData.email}
          onChange={handleChange}
        />
        {errors.email && (
          <p className="text-red-500 text-xs italic mt-1">{errors.email}</p>
        )}
      </div>
      <div>
        <label className="block text-gray-700 mb-2 uppercase font-[600] text-[14px]">
          Rating
        </label>
        <RatingStars
          rating={formData.rating || 0}
          onRatingChange={(newRating) =>
            setFormData({
              ...formData,
              rating: newRating,
            })
          }
          clearable={true}
        />
        {errors.rating && (
          <p className="text-red-500 text-xs italic mt-1">{errors.rating}</p>
        )}
      </div>
      <div>
        <label className="block text-gray-700 mb-2 uppercase font-[600] text-[14px]">
          Review Title
        </label>
        <input
          type="text"
          name="title"
          placeholder="Review Title"
          className="border-2 border-gray-300 p-2 rounded w-full"
          value={formData.title}
          onChange={handleChange}
        />
        {errors.title && (
          <p className="text-red-500 text-xs italic mt-1">{errors.title}</p>
        )}
      </div>
      <div>
        <label className="block text-gray-700 mb-2 uppercase font-[600] text-[14px]">
          Body of Review (1500)
        </label>
        <textarea
          name="content"
          placeholder="Body of Review (1500)"
          className="border-2 border-gray-300 p-2 rounded w-full"
          rows={5}
          value={formData.content}
          onChange={handleChange}
        />
      </div>

      <button
        type="submit"
        className="bg-black text-white p-2"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
};

export default RatingForm;
